// Client-side mirror of the stock maths in backend/app/services/stock.py (stock_state /
// stock_alert_percent / pack_size_of). The server is what counts stock down on
// a sale and what refuses one; this is so a tile can show "2 packets left"
// without another request. Keep the two in sync.
import { STOCKED_SOURCES, sourceOf } from "./cafeSources";

export const DEFAULT_STOCK_ALERT_PERCENT = 25;
export const MAX_PACK_SIZE = 200;

/** The configured warning threshold, clamped to something usable. */
export function stockAlertPercent(settings) {
  const value = Number(settings?.stock_alert_percent);
  if (!Number.isFinite(value)) return DEFAULT_STOCK_ALERT_PERCENT;
  return Math.min(90, Math.max(5, Math.round(value)));
}

/** Singles in one box — 12 cigarettes to a packet, 1 for a bottle. */
export function packSizeOf(item) {
  const size = Number(item?.pack_size);
  if (!Number.isFinite(size)) return 1;
  return Math.max(1, Math.min(MAX_PACK_SIZE, Math.round(size)));
}

/**
 * What is left of one item and whether it needs buying. Counts are held in the
 * unit that is sold (single cigarettes, single bottles) and read back as boxes,
 * so selling the twelfth cigarette of a packet takes a whole packet off.
 * An item nobody has entered stock for is not tracked: no count, no warning.
 */
export function stockState(item, alertPercent = DEFAULT_STOCK_ALERT_PERCENT) {
  const full = Number(item?.stock_full) || 0;
  const packSize = packSizeOf(item);
  if (!STOCKED_SOURCES.includes(sourceOf(item)) || full <= 0) {
    return { tracked: false, inStock: null, full: 0, reorderAt: 0, low: false, out: false,
             toRefill: 0, percentLeft: null, packSize, packsLeft: null, looseLeft: 0,
             packsFull: 0, packsToRefill: 0 };
  }
  const inStock = Number(item?.stock_qty) || 0;
  // Rounded up, so the last whole item still trips the warning.
  const reorderAt = Math.max(1, Math.ceil((full * alertPercent) / 100));
  const toRefill = Math.max(0, full - inStock);
  return {
    tracked: true,
    inStock,
    full,
    reorderAt,
    low: inStock <= reorderAt,
    out: inStock <= 0,
    toRefill,
    percentLeft: Math.max(0, Math.round((inStock / full) * 100)),
    packSize,
    packsLeft: Math.floor(Math.max(0, inStock) / packSize),
    looseLeft: Math.max(0, inStock) % packSize,
    packsFull: Math.ceil(full / packSize),
    packsToRefill: Math.ceil(toRefill / packSize),
  };
}

/** Tailwind classes for a stock badge — red when out, amber when low. */
export function stockTone(state) {
  if (!state.tracked) return "text-zinc-600";
  if (state.out) return "text-red-400";
  if (state.low) return "text-amber-400";
  return "text-zinc-400";
}

/**
 * "2 packets + 5" / "11 bottles" — what is left, in the words the shelf uses.
 * `unit` is the singular noun for one box ("packet", "bottle").
 */
export function describeStock(state, unit = "pack") {
  if (!state.tracked) return "Stock not counted";
  if (state.out) return "Out of stock";
  if (state.packSize === 1) {
    return `${state.inStock} ${unit}${state.inStock === 1 ? "" : "s"} left`;
  }
  // Down to the last open box, say so plainly rather than "0 packets + 8".
  if (state.packsLeft === 0) return `${state.looseLeft} loose left`;
  const packs = `${state.packsLeft} ${unit}${state.packsLeft === 1 ? "" : "s"}`;
  return state.looseLeft > 0 ? `${packs} + ${state.looseLeft} loose` : `${packs} left`;
}

/**
 * The same two phrasings for a row straight off the API (snake_case), so the
 * refill banner, the Cafe tiles and the Stock table all read alike.
 */
export function describeRowStock(row, unit = "pack") {
  if (!row?.tracked) return "—";
  const size = Number(row.pack_size) || 1;
  if (size === 1) return `${row.in_stock} / ${row.full}`;
  const of = ` / ${row.packs_full} ${unit}${row.packs_full === 1 ? "" : "s"}`;
  if (row.packs_left === 0) return `${Math.max(0, row.in_stock)} loose${of}`;
  const loose = row.loose_left > 0 ? ` + ${row.loose_left}` : "";
  return `${row.packs_left}${loose}${of}`;
}

/** "buy 2 packets" — always in whole boxes, which is how stock is bought. */
export function refillPhrase(row, unit = "pack") {
  const packs = row?.packs_to_refill ?? 0;
  return `buy ${packs} ${unit}${packs === 1 ? "" : "s"}`;
}

/** "8 loose left" / "3 packets + 11 loose" for an API row. */
export function shortRowStock(row, unit = "pack") {
  if (!row?.tracked) return "not counted";
  if (row.out) return "out of stock";
  const size = Number(row.pack_size) || 1;
  if (size === 1) return `${row.in_stock} left`;
  if (row.packs_left === 0) return `${row.in_stock} loose left`;
  const loose = row.loose_left > 0 ? ` + ${row.loose_left}` : "";
  return `${row.packs_left}${loose} ${unit}${row.packs_left === 1 && !row.loose_left ? "" : "s"} left`;
}
